/**
 * Connect Claude (MCP) OAuth helpers — session-scoped, not company-scoped.
 *
 * `fetchOAuthClientInfo` describes the app asking for access (name + where the
 * code will be sent) so the consent page can say who is asking.
 * `submitOAuthConsent` answers Better Auth's consent step; the returned URL is
 * the client's redirect URI carrying either the code or `error=access_denied`.
 */

import { authClient } from "../auth-client"
import { api } from "./client.ts"
import { unwrap } from "./errors.ts"

export interface IOAuthClientInfo {
  name: string
  redirectHosts: string[]
  disabled: boolean
}

export async function fetchOAuthClientInfo(clientId: string): Promise<IOAuthClientInfo> {
  const response = unwrap(await api.api.mcp["client-info"].get({ query: { client_id: clientId } }))
  return response.data
}

interface IConsentResponse {
  redirectURI: string
}

function isConsentResponse(value: unknown): value is IConsentResponse {
  return (
    typeof value === "object" &&
    value !== null &&
    "redirectURI" in value &&
    typeof (value as { redirectURI: unknown }).redirectURI === "string"
  )
}

export async function submitOAuthConsent(accept: boolean, consentCode: string | undefined): Promise<string> {
  const { data, error } = await authClient.$fetch<unknown>("/oauth2/consent", {
    method: "POST",
    body: { accept, consent_code: consentCode ?? null },
  })
  if (error) {
    const message =
      typeof error === "object" && error !== null && "message" in error && typeof error.message === "string"
        ? error.message
        : "Could not record your answer."
    throw new Error(message)
  }
  if (!isConsentResponse(data)) throw new Error("Could not record your answer.")
  return data.redirectURI
}
